// Level data + parser.
//
// ASCII legend:
//   '#'      solid tile
//   '-'      one-way platform (solid only when falling onto it from above)
//   '^'      spikes (kills the live player; echoes are immune)
//   '*'      spring pad — launches you far higher than a jump or echo-stool
//   'x'      crumble slab — solid, but collapses ~0.4s after a body stands on
//            it, then respawns a couple seconds later (resets each attempt)
//   'P'      player spawn (feet on the tile's bottom edge)
//   'E'      exit portal
//   '1'-'9'  floor buttons (HOLD-driven door: needs a body kept on the pad)
//   'a'-'d'  floor LEVER for the matching door 'A'-'D' — stepping on it FLIPS
//            the door open<->shut and the new state sticks (no hold needed)
//   'A'-'D'  door tiles. HOLD door: open while ALL buttons in its `doors` rule
//            are held. TOGGLE door (has a lever): open while flipped on.
//   '.'      empty space
//
// Geometry contract with the physics in sim.ts (TILE=30):
//   max jump rise  ~136px (4.5 tiles)  -> 5-tile walls need an echo step-stool
//   echo-head jump ~163px (5.4 tiles)  -> 6-tile walls are echo-proof
//   max jump span  ~5.5 tiles of horizontal gap at full speed

import type { LevelDef, ParsedLevel, ButtonDef, DoorDef, LeverDef } from './types';
import { TILE } from './types';

export const LEVELS: LevelDef[] = [
  // === PULSE TEACHING CURVE (signature mechanic) =============================
  // A TIMED SEAL gates the exit: a FRESH pad-tap opens it for a short window,
  // then it auto-shuts. A parked/stacked echo is a STEADY press (no rising
  // edge), so it can never re-arm the seal — stack-to-skip is structurally
  // dead. Win = touch the exit WHILE the seal is open. Each level teaches one
  // new idea; the solver verifies BOTH the intended relay AND that the cheese
  // fails (scripts/solve-check.mjs).

  // L1 — the seal exists; your echo opens it from afar while you race the window.
  {
    name: 'THE LATCH',
    subtitle: 'the exit is a heartbeat',
    hint: 'The gate opens for a heartbeat when its pad is freshly tapped — and you are too far to make it alone. Send an echo to tap it, then race the window.',
    echoes: 2,
    accent: '#ffd24b',
    accent2: '#d48a1f',
    doors: { A: [1] },
    timed: { A: 48 }, // gate stays open 48 ticks (~0.8s) after a rising-edge tap
    seal: 'A', // the exit only counts as won while door A is open
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '..................A.....',
      '..................A.....',
      '..................A.....',
      '..................A.....',
      '..................A.....',
      '....1......P......A.E...',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },
  // L2 — a longer relay: same idea, more ground to cover before the gate shuts.
  {
    name: 'THE REACH',
    subtitle: 'farther than one breath',
    hint: 'Same trick, more distance. The echo taps the pad as you run — but the exit is a long sprint, so do not dawdle.',
    echoes: 2,
    accent: '#ffb347',
    accent2: '#d4791f',
    doors: { A: [1] },
    timed: { A: 56 },
    seal: 'A',
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '...................A....',
      '...................A....',
      '...................A....',
      '...................A....',
      '...................A....',
      '.....1.......P.....A.E..',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },
  // L3 — the timed seal meets a classic HOLD door: one echo holds, one relays.
  {
    name: 'HOLD & PULSE',
    subtitle: 'two kinds of door',
    hint: 'A held pad keeps its door open while a body stays — but the SEAL only opens for a heartbeat. Hold one, relay the other.',
    echoes: 3,
    accent: '#5dd6ff',
    accent2: '#2b8fd4',
    doors: { A: [1], B: [2] },
    timed: { A: 56 },
    seal: 'A',
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '.................B.A....',
      '.................B.A....',
      '.................B.A....',
      '.................B.A....',
      '.................B.A....',
      '.....1...2...P...B.A.E..',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },
  // L4 — the relay across a void: your echo leaps to the pad while you race home.
  {
    name: 'OVER THE GAP',
    subtitle: 'leap, tap, race',
    hint: 'The pad is across the void. Your echo will leap to it and tap — be at the exit when it does.',
    echoes: 2,
    accent: '#54d6c0',
    accent2: '#2a8a78',
    doors: { A: [1] },
    timed: { A: 60 },
    seal: 'A',
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '...................A....',
      '...................A....',
      '...................A....',
      '...................A....',
      '...................A....',
      '.....1.......P.....A.E..',
      '#######...##############',
      '#######...##############',
      '#######...##############',
      '#######...##############',
    ],
  },
  // L5 — the relay runs the other way: pad on the right, exit on the left.
  {
    name: 'BACKBEAT',
    subtitle: 'the other direction',
    hint: 'This time the pad is behind you. Send the echo right to tap it, then break left for the exit.',
    echoes: 2,
    accent: '#ff7ad6',
    accent2: '#c43f9e',
    doors: { A: [1] },
    timed: { A: 48 },
    seal: 'A',
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '.......A................',
      '.......A................',
      '.......A................',
      '.......A................',
      '.......A................',
      '.....E.A.....P.......1..',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },
  // L6 — a tight window: short and sharp, no room to misjudge the timing.
  {
    name: 'THE SQUEEZE',
    subtitle: 'a narrow window',
    hint: 'Short gate, short fuse. The seal barely opens — your run and your echo must line up almost exactly.',
    echoes: 2,
    accent: '#ff6a4b',
    accent2: '#c43a1f',
    doors: { A: [1] },
    timed: { A: 40 },
    seal: 'A',
    grid: [
      '..................',
      '..................',
      '..................',
      '..................',
      '..................',
      '..................',
      '..................',
      '..................',
      '.............A....',
      '.............A....',
      '.............A....',
      '.............A....',
      '.............A....',
      '....1....P...A.E..',
      '##################',
      '##################',
      '##################',
      '##################',
    ],
  },
  // L7 — a two-pad seal: one echo HOLDS a pad, another TAPS to fire the window.
  {
    name: 'TWO TAPS',
    subtitle: 'both at once',
    hint: 'This seal needs two pads. Park an echo on one, then tap the other to fire the window as you run.',
    echoes: 3,
    accent: '#9b8bff',
    accent2: '#5b3fcf',
    doors: { A: [1, 2] },
    timed: { A: 56 },
    seal: 'A',
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '...................A....',
      '...................A....',
      '...................A....',
      '...................A....',
      '...................A....',
      '.....1...2...P.....A.E..',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },
  // L8 — capstone: a HOLD door AND a two-pad seal, every echo doing a job.
  {
    name: 'TANDEM',
    subtitle: 'every self a part',
    hint: 'One echo holds the door, one holds a seal pad, one taps the trigger — then you run the open road while the window lives.',
    echoes: 4,
    accent: '#ffffff',
    accent2: '#7a5fff',
    doors: { A: [1, 2], B: [3] },
    timed: { A: 56 },
    seal: 'A',
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '.................B.A....',
      '.................B.A....',
      '.................B.A....',
      '.................B.A....',
      '.................B.A....',
      '....1..2..3..P...B.A.E..',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },
  {
    name: 'THE CHASM',
    subtitle: 'mind the gap',
    hint: 'Build up speed and leap the void — there is no pad to help here.',
    echoes: 2,
    accent: '#5ad1c0',
    accent2: '#2a7d70',
    doors: {},
    grid: [
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '..P.............E...',
      '#########...########',
      '#########...########',
      '#########...########',
      '#########...########',
    ],
  },
  {
    name: 'THE WALL',
    subtitle: 'stand on yesterday',
    hint: 'Too tall to clear. Leave a self at its foot and climb your own head.',
    echoes: 2,
    accent: '#ffce54',
    accent2: '#c47a1a',
    doors: {},
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '..............#.........',
      '..............#.........',
      '..............#.........',
      '..............#.........',
      '..P...........#.....E...',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },
  {
    name: 'ACROSS AND BACK',
    subtitle: 'leap, seal, leap again',
    hint: 'Clear the void to the pad — then your echo holds the gate while you cross once more.',
    echoes: 2,
    accent: '#54c7ff',
    accent2: '#2a6fa8',
    doors: { A: [1] },
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '...............A........',
      '...............A........',
      '...............A........',
      '...............A........',
      '...............A........',
      '..P........1...A....E...',
      '######...###############',
      '######...###############',
      '######...###############',
      '######...###############',
    ],
  },
  {
    name: 'SPIKE RUN',
    subtitle: 'thorns in the page',
    hint: 'The spikes only bite at ground level — leap them, hold the pad, leap them again.',
    echoes: 2,
    accent: '#ff5a6e',
    accent2: '#a82a3a',
    doors: { A: [1] },
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '.................A......',
      '.................A......',
      '.................A......',
      '.................A......',
      '.................A......',
      '..P....^^^...1...A...E..',
      '########################',
      '########################',
      '########################',
      '########################',
    ],
  },
  {
    name: 'OVER AND UP',
    subtitle: 'a leap, then a climb',
    hint: 'Cross the gap, leave a self at the wall, and climb your own shoulders over it.',
    echoes: 2,
    accent: '#b6ff54',
    accent2: '#6fa82a',
    doors: {},
    grid: [
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..............#...........',
      '..............#...........',
      '..............#...........',
      '..............#...........',
      '..P...........#.....E.....',
      '######...#################',
      '######...#################',
      '######...#################',
      '######...#################',
    ],
  },
  {
    name: 'TWO VAULTS',
    subtitle: 'a pair of double locks',
    hint: 'Each vault needs two pads at once. Open the first pair to reach the second.',
    echoes: 5,
    accent: '#ffd54b',
    accent2: '#c4972a',
    doors: { A: [1, 2], B: [3, 4] },
    grid: [
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '...........A.........B........',
      '...........A.........B........',
      '...........A.........B........',
      '...........A.........B........',
      '...........A.........B........',
      '..P..1..2..A...3..4..B....E...',
      '##############################',
      '##############################',
      '##############################',
      '##############################',
    ],
  },
  {
    name: 'FOURFOLD',
    subtitle: 'the long corridor of gates',
    hint: 'Four gates, four pads, in order. Spend a self on each as you go.',
    echoes: 5,
    accent: '#54ffd1',
    accent2: '#2aa888',
    doors: { A: [1], B: [2], C: [3], D: [4] },
    grid: [
      '......................................',
      '......................................',
      '......................................',
      '......................................',
      '......................................',
      '......................................',
      '......................................',
      '......................................',
      '........A.....B.....C.....D...........',
      '........A.....B.....C.....D...........',
      '........A.....B.....C.....D...........',
      '........A.....B.....C.....D...........',
      '........A.....B.....C.....D...........',
      '..P..1..A..2..B..3..C..4..D.....E.....',
      '######################################',
      '######################################',
      '######################################',
      '######################################',
    ],
  },
  {
    name: 'TWIN CHASM',
    subtitle: 'two voids, no nets',
    hint: 'Two leaps, back to back. Keep your speed between them.',
    echoes: 2,
    accent: '#5ad1ff',
    accent2: '#2a7da8',
    doors: {},
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '..P.................E...',
      '######...#####...#######',
      '######...#####...#######',
      '######...#####...#######',
      '######...#####...#######',
    ],
  },
  {
    name: 'PIT RELAY',
    subtitle: 'hold two, across the gap',
    hint: 'Cross the gap to set both pads — the vault beyond needs both held.',
    echoes: 3,
    accent: '#ff8f54',
    accent2: '#a8552a',
    doors: { A: [1, 2] },
    grid: [
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..................A.......',
      '..................A.......',
      '..................A.......',
      '..................A.......',
      '..................A.......',
      '..P........1..2...A...E...',
      '######...#################',
      '######...#################',
      '######...#################',
      '######...#################',
    ],
  },
  {
    name: 'GAUNTLET',
    subtitle: 'void, then thorns',
    hint: 'A pit, then a strip of spikes. Two clean leaps and the page is yours.',
    echoes: 2,
    accent: '#ff5454',
    accent2: '#a82a2a',
    doors: {},
    grid: [
      '......................',
      '......................',
      '......................',
      '......................',
      '......................',
      '......................',
      '......................',
      '......................',
      '......................',
      '......................',
      '......................',
      '......................',
      '......................',
      '..P..........^^....E..',
      '######...#############',
      '######...#############',
      '######...#############',
      '######...#############',
    ],
  },
  {
    name: 'DEEP RELAY',
    subtitle: 'a gauntlet of seals',
    hint: 'Cross the void again and again, setting each pad until the deep vault yields.',
    echoes: 4,
    accent: '#54a8ff',
    accent2: '#2a5fa8',
    doors: { A: [1], B: [2, 3] },
    grid: [
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '.............A.........B......',
      '.............A.........B......',
      '.............A.........B......',
      '.............A.........B......',
      '.............A.........B......',
      '..P........1..A..2..3...B..E..',
      '######...#####################',
      '######...#####################',
      '######...#####################',
      '######...#####################',
    ],
  },
  {
    name: 'FINALE',
    subtitle: 'everything you have learned',
    hint: 'Seal the twin vault, leap the void, clear the thorns. One last loop.',
    echoes: 3,
    accent: '#ffffff',
    accent2: '#d44bff',
    doors: { A: [1, 2] },
    grid: [
      '..................................',
      '..................................',
      '..................................',
      '..................................',
      '..................................',
      '..................................',
      '..................................',
      '..................................',
      '............A.....................',
      '............A.....................',
      '............A.....................',
      '............A.....................',
      '............A.....................',
      '..P..1..2...A.............^^...E..',
      '##################...#############',
      '##################...#############',
      '##################...#############',
      '##################...#############',
    ],
  },

  // === PULSE — LATE ARC (timed seals meet traversal + multi-pad) ==============
  // The old flat "hold N pads" vaults, rebuilt as harder PULSE puzzles so the
  // late game is non-repetitive AND uncheeseable. Each is solver-verified for
  // the intended relay AND an anti-cheese assertion (scripts/solve-check.mjs).
  // L21 — OUTRUN: arm the seal, then sprint-leap the void to the exit in time.
  {
    name: 'OUTRUN',
    subtitle: 'race your own window',
    hint: 'Tap the pad, then sprint — the exit is across the void and the gate is already closing.',
    echoes: 2,
    accent: '#ffcf4b',
    accent2: '#d48a1f',
    doors: { A: [1] },
    timed: { A: 64 },
    seal: 'A',
    grid: [
      '.........................',
      '.........................',
      '.........................',
      '.........................',
      '.........................',
      '.........................',
      '.........................',
      '.........................',
      '.....................A...',
      '.....................A...',
      '.....................A...',
      '.....................A...',
      '.....................A...',
      '.....1.......P.......A.E.',
      '#################...#####',
      '#################...#####',
      '#################...#####',
      '#################...#####',
    ],
  },
  // L22 — THORNPASS: same dash, but thorns bar the run to the gate.
  {
    name: 'THORNPASS',
    subtitle: 'thorns on the clock',
    hint: 'The seal opens far away — and the path to it is lined with thorns. Leap them clean, the window is short.',
    echoes: 2,
    accent: '#ff5a6e',
    accent2: '#a82a3a',
    doors: { A: [1] },
    timed: { A: 64 },
    seal: 'A',
    grid: [
      '.........................',
      '.........................',
      '.........................',
      '.........................',
      '.........................',
      '.........................',
      '.........................',
      '.........................',
      '.....................A...',
      '.....................A...',
      '.....................A...',
      '.....................A...',
      '.....................A...',
      '.....1.......P..^^^..A.E.',
      '#########################',
      '#########################',
      '#########################',
      '#########################',
    ],
  },
  // L23 — TWIN GATE: a two-pad seal over a long run — hold one, tap the other.
  {
    name: 'TWIN GATE',
    subtitle: 'two pads, one window',
    hint: 'Park a self on one pad, then tap the far one to fire the seal — and be deep down the corridor when it does.',
    echoes: 3,
    accent: '#9b8bff',
    accent2: '#5b3fcf',
    doors: { A: [1, 2] },
    timed: { A: 60 },
    seal: 'A',
    grid: [
      '............................',
      '............................',
      '............................',
      '............................',
      '............................',
      '............................',
      '............................',
      '............................',
      '.......................A....',
      '.......................A....',
      '.......................A....',
      '.......................A....',
      '.......................A....',
      '......1....2...P.......A.E..',
      '############################',
      '############################',
      '############################',
      '############################',
    ],
  },
  // L24 — THE VAULT: capstone — a HOLD door AND a two-pad seal, every self a part.
  {
    name: 'THE VAULT',
    subtitle: 'every self a part',
    hint: 'One self holds the door, one holds a seal pad, one taps the trigger — then you run the open road while the window lives.',
    echoes: 4,
    accent: '#ffffff',
    accent2: '#7a5fff',
    doors: { A: [1, 2], B: [3] },
    timed: { A: 60 },
    seal: 'A',
    grid: [
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '...................B.A....',
      '...................B.A....',
      '...................B.A....',
      '...................B.A....',
      '...................B.A....',
      '.....1..2..3...P...B.A.E..',
      '##########################',
      '##########################',
      '##########################',
      '##########################',
    ],
  },

  // === NEW MECHANICS ARC =====================================================
  // Three fresh ideas, each taught clean then combined: LEVERS (flip a gate and
  // it stays — no echo spent holding it), SPRING PADS (launch past any climb),
  // CRUMBLE SLABS (footing that falls if you linger). See the legend up top.

  // --- LEVER intro: one throw, no echo needed --------------------------------
  {
    name: 'SWITCHBACK',
    subtitle: 'a gate that stays',
    hint: 'Step on the lever — it FLIPS the gate open and stays there. No echo needed.',
    echoes: 2,
    accent: '#46e6c8',
    accent2: '#1f9e86',
    doors: {},
    grid: [
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '....................',
      '..........A.........',
      '..........A.........',
      '..........A.........',
      '..........A.........',
      '..........A.........',
      '..P..a....A.....E...',
      '####################',
      '####################',
      '####################',
      '####################',
    ],
  },
  // --- LEVER chain: two throws, zero echoes (contrast THREEFOLD) -------------
  {
    name: 'DOUBLE THROW',
    subtitle: 'two gates, no past selves',
    hint: 'Throw each lever as you pass. Toggles cost no echoes — spend none here.',
    echoes: 2,
    accent: '#9be0ff',
    accent2: '#3f8fcf',
    doors: {},
    grid: [
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '..........................',
      '........A........B........',
      '........A........B........',
      '........A........B........',
      '........A........B........',
      '........A........B........',
      '..P..a..A....b...B....E...',
      '##########################',
      '##########################',
      '##########################',
      '##########################',
    ],
  },
  // --- SPRING intro: reach a ledge nothing else can ---------------------------
  {
    name: 'LAUNCHPAD',
    subtitle: 'up where you cannot climb',
    hint: 'That ledge is echo-proof. Step on the spring pad and ride the launch up.',
    echoes: 2,
    accent: '#5af0b4',
    accent2: '#22a878',
    doors: {},
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '..............E.........',
      '...........#######......',
      '........................',
      '........................',
      '........................',
      '........................',
      '..P.....................',
      '#########*##############',
      '########################',
      '########################',
      '########################',
    ],
  },
  // --- SPRING vault: sail over an echo-proof wall -----------------------------
  {
    name: 'OVERLEAP',
    subtitle: 'sail clean over',
    hint: 'No climb clears this wall. Spring up and arc over the top to the far side.',
    echoes: 2,
    accent: '#b6ff54',
    accent2: '#6fa82a',
    doors: {},
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '............#...........',
      '............#...........',
      '............#...........',
      '............#...........',
      '............#...........',
      '..P.........#.....E.....',
      '########*###############',
      '########################',
      '########################',
      '########################',
    ],
  },
  // --- CRUMBLE intro: keep moving or fall -------------------------------------
  {
    name: 'COLLAPSE',
    subtitle: 'do not linger',
    hint: 'This bridge falls the moment you stand still. Keep moving — cross in one go.',
    echoes: 2,
    accent: '#ff9a4b',
    accent2: '#c46a1a',
    doors: {},
    grid: [
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '........................',
      '..P...............E.....',
      '######xxxxxx############',
      '######......############',
      '######......############',
      '######^^^^^^############',
    ],
  },
  // --- COMBO finale: bridge -> lever -> spring --------------------------------
  {
    name: 'CASCADE',
    subtitle: 'everything, in one breath',
    hint: 'Cross the falling bridge, throw the lever for the gate, then spring for the exit.',
    echoes: 3,
    accent: '#ffffff',
    accent2: '#ff7a54',
    doors: {},
    grid: [
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '..............................',
      '..........................E...',
      '.................A......######',
      '.................A............',
      '.................A............',
      '.................A............',
      '.................A............',
      '..P..........a...A............',
      '#####xxxxx############*#######',
      '#####.....####################',
      '#####.....####################',
      '#####^^^^^####################',
    ],
  },
];

/** Parse a LevelDef's ASCII grid into structured data the sim can run. */
export function parseLevel(def: LevelDef): ParsedLevel {
  const rows = def.grid.length;
  const cols = def.grid[0]!.length;
  for (const row of def.grid) {
    if (row.length !== cols) {
      throw new Error(`level "${def.name}": ragged grid row (expected ${cols}, got ${row.length})`);
    }
  }

  const tiles: string[][] = [];
  const buttons: ButtonDef[] = [];
  const levers: LeverDef[] = [];
  const crumbles: Array<{ col: number; row: number }> = [];
  const doorCells = new Map<string, Array<{ col: number; row: number }>>();
  let spawn = { x: TILE, y: TILE };
  let exit = { col: 0, row: 0 };

  for (let r = 0; r < rows; r++) {
    const rowStr = def.grid[r]!;
    const out: string[] = [];
    for (let c = 0; c < cols; c++) {
      const ch = rowStr[c]!;
      if (ch === 'P') {
        spawn = { x: c * TILE + TILE / 2, y: (r + 1) * TILE };
        out.push('.');
      } else if (ch === 'E') {
        exit = { col: c, row: r };
        out.push('.');
      } else if (ch >= '1' && ch <= '9') {
        buttons.push({ id: Number(ch), col: c, row: r });
        out.push('.');
      } else if (ch >= 'a' && ch <= 'd') {
        levers.push({ letter: ch.toUpperCase(), col: c, row: r });
        out.push('.');
      } else if (ch >= 'A' && ch <= 'D') {
        const cells = doorCells.get(ch) ?? [];
        cells.push({ col: c, row: r });
        doorCells.set(ch, cells);
        out.push(ch);
      } else if (ch === 'x') {
        crumbles.push({ col: c, row: r });
        out.push('x');
      } else {
        out.push(ch);
      }
    }
    tiles.push(out);
  }

  const leverLetters = new Set(levers.map((l) => l.letter));
  for (const l of leverLetters) {
    if (!doorCells.has(l)) throw new Error(`level "${def.name}": lever ${l.toLowerCase()} has no door ${l}`);
  }

  const timed = def.timed ?? {};
  const doors: DoorDef[] = [];
  for (const [letter, cells] of doorCells) {
    const toggle = leverLetters.has(letter);
    const isTimed = Object.prototype.hasOwnProperty.call(timed, letter);
    const needs = def.doors[letter];
    if (isTimed) {
      if (toggle) throw new Error(`level "${def.name}": door ${letter} can't be a lever-toggle AND a timed seal`);
      if (!needs || needs.length === 0) throw new Error(`level "${def.name}": timed seal ${letter} needs a button rule in "doors"`);
      const win = timed[letter];
      // A non-positive / non-integer window would arm to <=0 and the seal could
      // never open (silently unsolvable) — and `?? 42` does NOT catch 0 since
      // `0 ?? 42 === 0`. Fail loudly at parse time instead.
      if (win === undefined || !Number.isInteger(win) || win <= 0)
        throw new Error(`level "${def.name}": timed seal ${letter} needs a positive integer windowTicks (got ${win})`);
      doors.push({ letter, cells, needs, mode: 'timed', windowTicks: win });
    } else if (toggle) {
      doors.push({ letter, cells, needs: needs ?? [], mode: 'toggle' });
    } else {
      if (!needs) throw new Error(`level "${def.name}": door ${letter} has no button rule`);
      doors.push({ letter, cells, needs, mode: 'hold' });
    }
  }

  // The exit seal MUST be a timed door — a hold/toggle seal could be propped open
  // forever by a parked echo, which would re-open the stack-to-the-exit cheese.
  if (def.seal !== undefined) {
    const sealDoor = doors.find((d) => d.letter === def.seal);
    if (!sealDoor) throw new Error(`level "${def.name}": seal ${def.seal} names no door`);
    if (sealDoor.mode !== 'timed') throw new Error(`level "${def.name}": seal ${def.seal} must be a timed door (add it to "timed")`);
  }

  return { def, cols, rows, tiles, spawn, exit, buttons, levers, doors, crumbles };
}
